<template>
  <Teleport :to="teleportTarget">
    <div
      class="absolute z-80000 pointer-events-none w-full h-full inset-0"
    >
      <transition name="fade" appear>
        <div
          v-if="active"
          key="modal-overlay"
          :class="overlayClass"
          @click="handleClick()"
        />
      </transition>
      <transition name="pop" appear>
        <div
          v-if="active"
          key="modal-container"
          class="absolute m-auto w-full h-full z-80000 flex items-center justify-center pointer-events-none py-4"
          role="modal"
        >
          <div :class="modalClass">
            <div class="flex h-full w-full">
              <div
                v-if="$slots.sidebar"
                :class="[
                  sidebarClass,
                  $auth.theme.light.gradient && $colorMode.value === 'light' ? $auth.theme.light.gradient : `bg-primary-${$auth.theme.light.shade}`,
                  $auth.theme.dark.gradient ? $auth.theme.dark.gradient : `dark:bg-gray-900`,
                ]"
              >
                <slot name="sidebar" :active="active" />
              </div>
              <div :class="[ $slots.sidebar ? 'w-2/3 flex-grow' : 'w-full', contentClass]">
                <div v-if="$slots.header || title" class="border-b px-3 py-2 flex items-center justify-between">
                  <slot name="header" :close="close">
                    <div>
                      <h4 class="font-semibold text-md leading-none">
                        {{ title }}
                      </h4>
                    </div>

                    <div class="flex items-center space-x-2">
                      <slot name="header-right" />
                      <o-button icon="x" flat @click="close" />
                    </div>
                  </slot>
                </div>

                <slot name="body">
                  <div class="px-3 pt-5 sm:px-4 flex-grow flex-shrink min-h-0">
                    <div v-if="$slots.default" :class="slotClass">
                      <slot :active="active" :close="close" />
                    </div>
                  </div>
                </slot>
                <div v-if="options.length || $slots.footer" class="px-3 pb-3 sm:px-4 flex space-x-2">
                  <slot name="footer">
                    <o-button
                      v-for="(button, index) in buttons"
                      :key="index"
                      class="w-full h-9"
                      :variant="button.variant"
                      :flat="button.flat"
                      :outline="button.outline"
                      :disabled="button.disabled || isLoading"
                      @click="handleClick(button)"
                    >
                      {{ button.text }}
                    </o-button>
                  </slot>
                </div>
              </div>
            </div>

            <transition name="fade">
              <div v-if="isLoading" class="h-full w-full inset-0 absolute bg-white/70 dark:bg-gray-900/50 z-10 flex items-center justify-center">
                <slot name="loader">
                  <o-loader />
                </slot>
              </div>
            </transition>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script lang="ts">
export default { name: 'OModal' }
</script>

<script setup lang="ts">
const { $i18n } = useNuxtApp()

interface Props {
  options?: any[]
  portal?: string | null
  container?: string | object | null
  valid?: Function
  title?: string | null
  info?: string | null
  modalClass?: string
  overlayClass?: string
  sidebarClass?: string
  contentClass?: string
  slotClass?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  portal: 'modal-container',
  container: null,
  valid: () => () => true,
  title: null,
  info: null,
  modalClass: 'relative m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-lg sm:w-full h-95vh max-h-full min-[2560px]:max-h-xl',
  overlayClass: 'absolute inset-0 w-full h-full bg-black opacity-50 rounded-md z-80000 transition pointer-events-auto',
  sidebarClass: 'w-1/3 px-5 py-5 text-white',
  contentClass: 'flex flex-col',
  slotClass: 'h-full',
  loading: false,
})

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: string): void
}>()

const active = ref(false)
const internalLoading = ref(false)
const isLoading = computed(() => props.loading || internalLoading.value)
const teleportTarget = computed(() => {
  const target = props.portal?.trim()
  if (!target) {
    return '#modal-container'
  }

  return target.startsWith('#') ? target : `#${target}`
})

const defaults: Record<string, any> = {
  confirm: {
    text: $i18n.t('general.confirm'),
    variant: 'primary',
    outline: false,
    flat: false,
    response: 'confirm',
    validate: true,
  },
  cancel: {
    text: $i18n.t('general.cancel'),
    variant: undefined,
    outline: true,
    flat: true,
    response: 'cancel',
    validate: false,
  },
}

const buttons = computed(() => {
  return props.options.map((option: any) => {
    return Object.assign({ ...defaults[option.type] }, option)
  })
})

async function handleClick (options: any = { response: 'cancel' }) {
  if (isLoading.value) {
    return
  }

  // Lock before validation yields so two clicks in the same frame cannot
  // start the same async callback more than once.
  internalLoading.value = true

  try {
    const isValid = !options.validate || await props.valid()

    if (isValid) {
      emit(options.response)

      const callback = options.callback

      if (callback) {
        const success = await callback()

        if (success) {
          close()
        }
      } else {
        close()
      }
    }
  } finally {
    internalLoading.value = false
  }
}

function open () {
  emit('open')
  active.value = true
}

function close () {
  emit('close')
  active.value = false
}

defineExpose({ open, close })
</script>

<style>
.pop-enter-active,
.pop-leave-active {
  transition: transform 0.3s, opacity 0.3s
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: scale(0.3);
}
</style>
